import { getSnapshot, getRoot, types as t, IAnyModelType, Instance, IArrayType } from '@invisr/mobx-state-tree'
import crudActions from './crud-actions'
import { observable, action, runInAction, toJS, makeObservable, IObservableArray } from 'mobx'
import fetch from '../../utils/fetch'
import Mapping from './mapping'
import Field from './field'
import DataTransformer from './filter'
import type { FieldEntry, LabelEntry } from './types'
import { CDN_URL } from '../../../env-public'
export class FilesProvider {
  data = [] as IObservableArray
  config

  constructor (config) {
    makeObservable(this, {
      data: observable,
      remove: action,
      query: action
    })

    this.config = config
  }

  remove = async _items => {
    if (!_items || !_items.length) return
    const items = toJS(_items)

    await Promise.all(items.map(item => fetch(`/api/datasource/${this.config.dbKey}/file/`, {
        method: 'DELETE',
        body: JSON.stringify({ Key: item.Key }),
        headers: { 'Content-Type': 'application/json' }
      }
    )))

    runInAction(() => {
      for (const item of items) {
        this.data.remove(this.data.find(o => o._id === item._id))
      }
    })
  }

  search = async searchValue => {
    return (
      this.data &&
      this.data.filter(item => {
        const strItem = JSON.stringify(item).toLowerCase()
        return strItem.indexOf(searchValue.toLowerCase()) !== -1
      })
    )
  }

  query = async context => {
    const self = this.config
    const rules = self.dataTransformer?.where?.rules ?? []
    let qFields: { referenceId?: string, folderPath?: string } = {}
    for (const whereRule of rules) {
      if (whereRule.leftField?.fieldPath && whereRule.rightValue) {
        qFields[whereRule.leftField.fieldPath] = await whereRule.rightValue.mapper.convertProps()
      }
    }

    const path = [qFields.referenceId, qFields.folderPath].filter(p => p?.length).join('/')
    const r = await fetch(
      `/api/datasource/${this.config.dbKey}/query/${encodeURIComponent(path)}`
    )
    if (!r.ok) return []
    let result = await r.json()
      
    if (self.dataTransformer) {
      result = self.dataTransformer.transform(result)
    }
    const Record = this.config.mapping.ItemClass
    const records = result.map(r => new Record(r))
    runInAction(() => this.data.replace(records))
    return records
  }

  upload = async ({ referenceId = '', folderPath = '', file, isPrivate = false, progress, onFileInfoAcquired, onUploadComplete, isAssetPublic = false}) => {
    const xhr = new XMLHttpRequest()
    const folder = folderPath.split('/').filter(p => p && p.length).map(item => encodeURIComponent(item)).join('/')
    let fileName = [referenceId, folder, file.name].filter(p => p && p.length).join('/')
    fileName = encodeURIComponent(fileName)
    const pathSuffix = isPrivate ? '&private=true' : ''
    const fullPath = `/api/datasource/${
      this.config.dbKey
    }/params?filename=${fileName}${pathSuffix}&isAssetPublic=${isAssetPublic}`

    let fileUrl = `/api/datasource/${this.config.dbKey}/file/` + (isPrivate ? 'private/' : 'public/') + encodeURIComponent(fileName)
    
    onFileInfoAcquired?.({ fileName: file.name, fileUrl }, false)

    const res = await fetch(fullPath, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      mode: 'cors'
    })
    const fileResponse = await res.json()
    const formData = new FormData()
    Object.keys(fileResponse.fields).forEach(fieldKey => {
      formData.append(fieldKey, fileResponse.fields[fieldKey])
    })
    formData.append('file', file)
    xhr.open('post', fileResponse.url)
    xhr.upload.onprogress = e =>
      progress && progress(e.lengthComputable, e.loaded, e.total)
    xhr.onload = () => {
      const { acl, key } = fileResponse?.fields ?? {}
      onFileInfoAcquired?.({ fileName: file.name, fileUrl: acl === 'public-read' ? `${CDN_URL}/${key}` : fileUrl })
      onUploadComplete?.(fileResponse)
      return { id: file.name, file }
    }
    xhr.send(formData)
  }

  updateAssets = async (batchedAssets) => {
    await fetch(`/api/datasource/${this.config.dbKey}/files`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      mode: 'cors',
      body: JSON.stringify(batchedAssets)
    })
  }
}

export default t
  .model('Files', {
    hidden: t.optional(t.boolean, false),
    type: t.optional(t.string, 'Files'),
    dataTransformer: t.late(() => t.maybeNull(DataTransformer)),
    fields: t.maybeNull(t.array(Field))
  })
  .views(self => ({
    get icon() {
      return 'document'
    },
    get provider () {
      return FilesProvider
    }
  }))
  .views(self => ({
    get mapping () {
      const f = self.fields.find(f => f.type === 'REFERENCE')
      return Mapping.create(
        {
          key: 'referenceId',
          type: 'reference',
          bindingType: 'data',
          dataSource: (self as any).id,
          value: getSnapshot(f)
        },
        { getOrLoadObject: (getRoot(self) as any).getOrLoadObject, parentDataSource: self }
      )
    },
    getFields (dsId) {
      if (!dsId) return self.fields
      const ds = (getRoot(self) as any).dataSources.get(dsId)
      return (ds && ds.getFields && ds.getFields()) || []
    },
    labels (): LabelEntry {
      const labelFields = (self as any).getFields().map(f => ({
          ...f,
          meta: { type: f.type },
          key: f.fieldPath,
          fieldType: f.type
      }))
      return {
        key: (self as any).id,
        label: (self as any).name,
        childNodes: [
          {
            key: 'methods',
            label: 'Methods',
            childNodes: [
              {
                key: 'remove',
                label: 'remove',
                secondaryLabel: 'FUNCTION'
              },
              {
                key: 'query',
                label: 'query',
                secondaryLabel: 'FUNCTION'
              },
              {
                key: 'search',
                label: 'search',
                secondaryLabel: 'FUNCTION'
              },
              {
                key: 'upload',
                label: 'upload',
                secondaryLabel: 'FUNCTION'
              }
            ]
          },
          {
            key: 'data',
            label: 'Data',
            childNodes: labelFields
          }
        ]
      }
    }
  }))
  .actions(crudActions)
  .actions(self => ({
    afterCreate () {
      self.fields = self.fields || [
        { type: 'ID', fieldPath: '_id', label: 'id' },
        { type: 'STRING', fieldPath: 'name', label: 'name' },
        { type: 'INTEGER', fieldPath: 'size', label: 'size' },
        { type: 'URL', fieldPath: 'url', label: 'url' },
        { type: 'REFERENCE', fieldPath: 'referenceId', label: 'referenceId' },
        { type: 'BOOLEAN', fieldPath: 'private', label: 'private' }
      ] as any
    }
  }))
